import React, { useCallback, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { useTranslation } from "react-i18next"
import theme from "lib/material-ui/theme"
import { styled } from "@mui/material/styles"
import Stack from "@mui/material/Stack"
import Box from "@mui/material/Box"
import { SIGN_OUT_USER } from "constants/events"
import { getDaysUntil } from "utils/dateUtils"
import { PATH_INFOS } from "utils/navigatorUtils"
import { useAppSelector } from "appRedux/hooks"
import { useGetActiveSubscriptionQuery } from "appRedux/slices/premiumApiSlice"
import { updateForgetPasswordStep } from "appRedux/slices/resetPasswordSlice"
import { updateShowPremiumOnboarding } from "appRedux/slices/premiumSlice"
import { useAuth } from "context/AuthContext"
import SignOutIcon from "components/atoms/_icons/SignOutIcon"
import PasswordIcon from "components/atoms/_icons/PasswordIcon"
import PremiumLogo from "components/atoms/_icons/PremiumLogo"
import WarningRoundedIcon from "components/atoms/_icons/WarningRoundedIcon"
import Menu from "components/atoms/Menu"
import Dialog from "components/atoms/Dialog"
import WishamProButton from "components/atoms/WishamProButton"
import ResetPasswordFlow from "components/organisms/ResetPasswordFlow"

export default function SettingsPage() {
  const [showSignOutConfirmationDialog, setShowSignOutConfirmationDialog] = useState<boolean>(false)

  const isPremium = useAppSelector((state) => state.user.profile?.isPremium)
  const expireDate = useAppSelector((state) => state.premium.expireDate)

  const { isUserLogoutLoading } = useAuth()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const getActiveSubscriptionResult = useGetActiveSubscriptionQuery(undefined, {
    skip: !isPremium
  })

  const getPremiumButtonLabel = useCallback(() => {
    return (isPremium && expireDate) ? <ProButtonLabel>
      {t("premium.proUser")}
      <span className="farsi-digits">{t("general.remainingTime")} {getDaysUntil(new Date(), expireDate)} {t("general.daysLeft")}</span>
    </ProButtonLabel> : t("premium.upgradeToPro")
  }, [isPremium, expireDate])

  const topMenuItems = useMemo(() => ([
      {
        key: "premium",
        label: getPremiumButtonLabel(),
        icon: <PremiumLogo width={32} height={32}/>,
        actionButton: <WishamProButton repurchase={!!expireDate}/>,
        loading: getActiveSubscriptionResult.isLoading,
        menuStyle: {
          background: isPremium && !getActiveSubscriptionResult?.isLoading ? `linear-gradient(to right, ${theme.palette.primary[2]}, ${theme.palette.warning[1]})` : ""
        },
        onClick: handleUpgradeToProMenuClicked
      },
      {
        key: "change-password",
        label: t("auth.changePassword"),
        icon: <PasswordIcon/>,
        onClick: handleChangePasswordMenuClicked
      }
    ]
  ), [isPremium, expireDate, getActiveSubscriptionResult])

  // const bottomMenuItems = [
  // {
  //   label: t("settings.darkMode"),
  //   icon: <MoonIcon/>,
  //   switchable: true,
  //   defaultSwitchValue: false
  // },
  // {
  //   label: t("settings.notifications"),
  //   icon: <NotificationIcon/>,
  //   switchable: true,
  //   defaultSwitchValue: false
  // }
  // ]

  function handleUpgradeToProMenuClicked() {
    if (!isPremium) {
      dispatch(updateShowPremiumOnboarding(true))
    } else {
      navigate(PATH_INFOS.premiumSubscription.path)
    }
  }

  function handleChangePasswordMenuClicked() {
    dispatch(updateForgetPasswordStep(0))
  }

  async function handleSignOutButtonClicked() {
    setShowSignOutConfirmationDialog(true)
  }

  function handleCloseSignOutConfirmationDialog() {
    setShowSignOutConfirmationDialog(false)
  }

  async function handleSignOut() {
    document.dispatchEvent(new CustomEvent(SIGN_OUT_USER))
  }

  return (
    <>
      <Box sx={{ marginBottom: "10px" }}>
        <Menu menuItems={topMenuItems}/>
      </Box>
      {/*<Menu menuItems={bottomMenuItems}/>*/}
      <SignOutButton onClick={handleSignOutButtonClicked}>
        <SignOutIcon/>
        {t("auth.signOut")}
      </SignOutButton>
      <Dialog
        message={t("auth.signOutConfirmationQuestion")}
        icon={<WarningRoundedIcon/>}
        buttons={[
          { label: t("general.yes"), mode: "white", loading: isUserLogoutLoading, onClick: handleSignOut },
          { label: t("general.no"), mode: "primary", loading: false, onClick: handleCloseSignOutConfirmationDialog }
        ]}
        open={showSignOutConfirmationDialog}
        disableBackdropClose={true}
        onClose={handleCloseSignOutConfirmationDialog}
      />
      <ResetPasswordFlow/>
    </>
  )
}

const SignOutButton = styled(Stack)(({ theme }) => ({
  height: "48px",
  flexDirection: "row",
  justifyContent: "center",
  alignItems: "center",
  background: "#FFFFFF",
  gap: "4px",
  padding: "8px 14px",
  fontSize: "1.4rem",
  color: theme.palette.error["4"],
  margin: "auto 16px 30px",
  borderRadius: "8px",
  boxShadow: `0 0 10px 0 ${theme.palette.gray["4"]}`,

  "& svg": {
    stroke: theme.palette.error["4"]
  }
}))

const ProButtonLabel = styled(Stack)(({ theme }) => ({
  color: theme.palette.primary[6],

  "& span": {
    color: theme.palette.gray[13],
    fontSize: "1.1rem",
    fontWeight: 400
  }
}))
